import { DateTime } from 'luxon';
import { Log_Entry, LogRecordState, LogRecordType, getEntryLocalTimeInDesiredTimeZone } from '@cf-shared/data-model/user-log';
import { SettingsService } from './settings.service';
import { describeError } from '@cf-shared/error-describe';
import { timeout } from '@cf-shared/shared-util';
import { wasReportedByCloudFunctions } from './cloud-functions-errors';

export const APP_TITLE = 'MH Physique';

// fire notifications 10 minutes before the scheduled time (same default as Google Calendar / Apple Calendar)
export const ADVANCE_MINUTES = 10;

export const WEEKDAY_NAMES = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

// djb2 hash — produces a stable 32-bit integer from a string
export function stringToNotificationId(str: string): number {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

export function formatTimeForId(hours: number, minutes: number): string {
  return `${String(hours).padStart(2, '0')}${String(minutes).padStart(2, '0')}`;
}

// The `type` half of a reminder's stringId. It is a log type for a real reminder, and the
// literal `test` for the test notification the user can send themselves from settings.
export type ReminderIdType = LogRecordType | 'test';

// What a reminder's stringId says about WHEN it is for. Deliberately no type: see
// parseReminderStringId below for why the type half is not read back.
export interface ParsedReminderStringId {
  weekday: number; // luxon weekday: 1=mon .. 7=sun
  hour: number;
  minute: number;
}

// THIS MODULE OWNS THE REMINDER stringId FORMAT — `{type}-{weekday}-{HHMM}`. Build one with
// buildReminderStringId and read one back with parseReminderStringId; never write either by
// hand. The id is the join key between three things that have to agree: the local
// notification the device schedules, the suppression key that cancels it once the user acts
// on the entry, and the tap resolver that finds the entry back when the user taps a push. A
// partial change to the format does not fail loudly — the builder and the suppressor just
// produce different ids, or the parser stops matching and a tap lands on the daily log
// instead of the entry. Keeping both directions in one place is what makes that impossible.
// See design-docs/push-notifications.md.

// Build a reminder's stringId from the ENTRY's local time — not the fire time, which is
// ADVANCE_MINUTES earlier. `DayEnd` is spelled `sleep` in the id, and that rename is part of
// the format, so it lives here.
export function buildReminderStringId(type: ReminderIdType, entryTime_local: DateTime): string {
  const typeName = type === LogRecordType.DayEnd ? 'sleep' : type;
  const weekdayName = WEEKDAY_NAMES[entryTime_local.weekday - 1];
  const timeStr = formatTimeForId(entryTime_local.hour, entryTime_local.minute);
  return `${typeName}-${weekdayName}-${timeStr}`;
}

// Read a reminder's stringId back into the weekday and local HH:MM it encodes. Returns null
// when the string is not in the format.
//
// It reads only the trailing `-{weekday}-{HHMM}` and tells the caller NOTHING about the type,
// on purpose: the type half varies (`test-` for test notifications, `sleep-` for DayEnd), and
// every caller already knows the real type from the push payload's `page`. The weekday list
// comes from WEEKDAY_NAMES so the names have a single source of truth with the builder above.
export function parseReminderStringId(stringId: string): ParsedReminderStringId | null {
  const match = stringId.match(new RegExp(`-(${WEEKDAY_NAMES.join('|')})-(\\d{2})(\\d{2})$`));
  if (!match) return null;
  return {
    weekday: WEEKDAY_NAMES.indexOf(match[1]) + 1,
    hour: parseInt(match[2], 10),
    minute: parseInt(match[3], 10),
  };
}

interface NotificationConfig {
  logType: LogRecordType;
  settingsKey: keyof SettingsService;
  contentKey: string;
  deepLinkPage: string;
}

const NOTIFICATION_CONFIGS: NotificationConfig[] = [
  {
    logType: LogRecordType.Workout,
    settingsKey: 'notificationsWorkout',
    contentKey: 'workout',
    deepLinkPage: 'training',
  },
  {
    logType: LogRecordType.Meal,
    settingsKey: 'notificationsMeals',
    contentKey: 'meal',
    deepLinkPage: 'meal',
  },
  {
    logType: LogRecordType.WeeklyCheckup,
    settingsKey: 'notificationsWeeklyCheckup',
    contentKey: 'weeklyCheckup',
    deepLinkPage: 'weekly-checkup',
  },
  {
    logType: LogRecordType.WeighIn,
    settingsKey: 'notificationsWeighIn',
    contentKey: 'weighIn',
    deepLinkPage: 'weigh-in',
  },
  {
    logType: LogRecordType.DayEnd,
    settingsKey: 'notificationsSleep',
    contentKey: 'sleep',
    deepLinkPage: 'home',
  },
];

export interface NotificationEvent {
  id: number;
  title: string;
  body: string;
  schedule: { at: Date };
  extra: { page: string; stringId: string; logEntryId: string; day: string };
}

// Map from push-payload `page` values (URL segments) to LogRecordType. Only types that
// correspond to a single log entry per notification need resolution here — `training`
// opens the day view and doesn't need an id; `home` / `coach-settings` aren't tied to entries.
const PAGE_TO_LOG_TYPE: Record<string, LogRecordType> = {
  meal: LogRecordType.Meal,
  'weigh-in': LogRecordType.WeighIn,
  'weekly-checkup': LogRecordType.WeeklyCheckup,
};

interface LogEntryLookup {
  getLogEntriesInRangeAsync(start: DateTime, end: DateTime, watcher: unknown, options?: { inLogEntriesTimeZone?: boolean }): Promise<Log_Entry[]>;
  database: { createDocWatcher(cb: (_: unknown) => void): { destroy(): void } };
}

// Given a push-notification `day` (ISO) + `stringId` (format `{type}-{weekday}-{HHMM}`),
// find the specific log entry the notification refers to. Matches by type (from `page`),
// local weekday, and local HH:MM (both from stringId) within a ±1-day UTC search window.
// Using weekday+HH:MM instead of calendar-date equality sidesteps the timezone skew when
// the tick's UTC calendar date differs from the user's local date (e.g. UTC+3 tapping at
// 01:30 local on a tick fired at 22:30 UTC prior day). stringId is unique per
// (type, weekday, HH:MM) within a week, and the ±1-day search window covers at most one
// match per triple. Returns undefined if no match.
export async function resolveLogEntryIdFromStringId(
  db: LogEntryLookup,
  day: string,
  stringId: string,
  page: string,
): Promise<string | undefined> {
  const logType = PAGE_TO_LOG_TYPE[page];
  if (!logType) return undefined;

  // read the weekday + local HH:MM back out of the stringId. the module-level parser owns the
  // format; it tells us nothing about the type because we already know that from `page`.
  const parsed = parseReminderStringId(stringId);
  if (!parsed) return undefined;
  const weekdayNum = parsed.weekday; // luxon weekday: 1=mon..7=sun
  const hour = parsed.hour;
  const minute = parsed.minute;

  // parse `day` in UTC explicitly — the server computes it as `now.toISODate()` in UTC at
  // tick time, and the ±1-day search window below must be UTC-centered to match the
  // semantic described in the comment
  const dayUtc = DateTime.fromISO(day, { zone: 'utc' });
  if (!dayUtc.isValid) return undefined;

  const watcher = db.database.createDocWatcher(() => {});
  try {
    // span one day on either side — `day` is computed in UTC at tick time, but entry
    // local time may fall into the previous/next UTC day for users far from UTC
    const searchStart = dayUtc.startOf('day').minus({ days: 1 });
    const searchEnd = dayUtc.endOf('day').plus({ days: 1 });
    const entries = await db.getLogEntriesInRangeAsync(searchStart, searchEnd, watcher);

    const match = entries.find(e => {
      if (e.type !== logType) return false;
      const entry_local = getEntryLocalTimeInDesiredTimeZone(e, dayUtc);
      // require same local weekday AND same HH:MM — weekday disambiguates between
      // adjacent days in the search window without depending on calendar-date equality
      // (which breaks when UTC day ≠ user's local day at tick time)
      if (entry_local.weekday !== weekdayNum) return false;
      return entry_local.hour === hour && entry_local.minute === minute;
    });
    return match?._id;
  } finally {
    watcher.destroy();
  }
}

// A tapped notification can reach the app two ways: a LOCAL notification this device
// scheduled for itself, and a SERVER push that arrived over FCM. Each route has its own
// pure decider below, because they do not offer the same destinations. What they share is
// this vocabulary of destinations, so that ONE table in notification.service.ts can say
// what each destination opens — see `NAV_HANDLERS` there.
//
// The deciders stay in this module, and the table does not, on purpose: this module has no
// page-class imports (see WORKOUT_DETAIL_PAGE_NAME below), which is what lets it be
// unit-tested without Angular.
//
// `unresolvedFallback: true` means the handler should land the user on the daily log AND
// emit a silent-problem report — never open an entry-editor with no subject. Entry-specific
// targets carry `day: string` (required) because the pages they route to require it; if the
// sender ever omitted `day`, the decider flips to the home fallback rather than crashing.
export type NavTarget =
  | { target: 'training';        day?: string }
  | { target: 'meal';             id: string; day: string }
  | { target: 'weigh-in';         id: string; day: string }
  | { target: 'weekly-checkup';   id: string }
  | { target: 'home';             day?: string; unresolvedFallback?: true }
  | { target: 'coach-settings' }
  | { target: 'referral-hub' }
  | { target: 'workout-detail';   workoutId: string }
  | { target: 'none' };

// The destinations a SERVER push can reach. It cannot reach `workout-detail`: only the rest
// timer opens that, and the rest timer is scheduled on the device.
export type PushNavTarget = Exclude<NavTarget, { target: 'workout-detail' }>;

// The destinations a LOCAL notification can reach. It cannot reach `coach-settings` or
// `referral-hub`: nothing schedules those on the device, they only ever arrive as a server
// push. That asymmetry is deliberate — do NOT "fix" it by widening either type.
export type LocalNavTarget = Exclude<NavTarget, { target: 'coach-settings' } | { target: 'referral-hub' }>;

// The destinations both routes share: the log-entry pages, plus the `none` answer for a
// destination name neither route recognises.
export type LogEntryNavTarget = Extract<NavTarget, { target: 'training' | 'meal' | 'weigh-in' | 'weekly-checkup' | 'home' | 'none' }>;

// Kept in one function so the "no resolved id means land on the daily log" rule is written
// once rather than once per route. Returns `none` for anything else, which each caller
// handles before reaching here.
function decideLogEntryNavigation(page: string | undefined, day: string | undefined, id: string | undefined): LogEntryNavTarget {
  // entry-specific pages need a resolved id; meal and weigh-in additionally need `day`
  // (their page params require it). if either is missing, land on the daily log and
  // flag the fallback so the handler can report it.
  const needsEntryId = page === 'meal' || page === 'weigh-in' || page === 'weekly-checkup';
  const needsDay = page === 'meal' || page === 'weigh-in';
  if (needsEntryId && (!id || (needsDay && !day))) {
    return { target: 'home', day, unresolvedFallback: true };
  }

  switch (page) {
    case 'training':        return { target: 'training', day };
    case 'meal':            return { target: 'meal', id: id!, day: day! };
    case 'weigh-in':        return { target: 'weigh-in', id: id!, day: day! };
    case 'weekly-checkup':  return { target: 'weekly-checkup', id: id! };
    case 'home':            return { target: 'home', day };
    default:                return { target: 'none' };
  }
}

/**
 * The log entry id a server push names in its own payload, or undefined when it names none.
 *
 * Reminder pushes carry no id: their entry is found from the day and the reminder time. A push
 * about ONE known entry carries the id as `logEntryId`, because it has no day or reminder time to
 * look anything up by. The coach's "check-up reviewed" push is the one that does so today
 * (#6433); before it did, every tap on it fell back to the home page.
 *
 * Anything that is not a non-empty piece of text answers undefined, so the tap takes the lookup
 * and its fallback exactly as it did before.
 */
export function readPushLogEntryId(data: { logEntryId?: unknown } | null | undefined): string | undefined {
  const id = data?.logEntryId;
  return typeof id === 'string' && id.length > 0 ? id : undefined;
}

// Pure routing decision for a push-notification tap. Given the server payload (page/day)
// and the resolver's output (id — may be undefined when resolution fails), returns a
// descriptor telling the handler where to navigate. Extracted to be unit-testable without
// mocking NavController / native listeners.
export function decidePushNavigation(page: string, day: string | undefined, id: string | undefined): PushNavTarget {
  switch (page) {
    case 'coach-settings':  return { target: 'coach-settings' };
    // #4963 — MIRRORED VALUE: REFERRAL_HUB_PUSH_PAGE in
    // server/functions/src/helpers/referral-notify-payload.ts. The destination also needs
    // an entry in NAV_HANDLERS (notification.service.ts) saying which page it opens — see
    // design-docs/referral-program.md, "Making the tap work". That entry is now a compile
    // error when missing, rather than a tap that silently does nothing.
    case 'referral-hub':    return { target: 'referral-hub' };
    default:                return decideLogEntryNavigation(page, day, id);
  }
}

// stable page name of the active-workout page (mirrors IWorkoutDetailPage.pageName).
// kept as a bare string here so this pure module stays free of page-ref imports,
// exactly like the deciders return plain string targets rather than PageRefs.
export const WORKOUT_DETAIL_PAGE_NAME = 'WorkoutDetailPage';

// the fields a local notification carries in its `extra` payload, as the tap handler reads
// them back. every one is optional here because the payload comes off the device and may
// have been written by an older version of the app.
export interface LocalNotificationExtra {
  page?: string;
  logEntryId?: string;
  day?: string;
  workoutId?: string;
}

// Pure routing decision for a tap on a LOCAL notification this device scheduled. Extracted
// to be unit-testable without mocking NavController / native listeners.
//
// The `workout-detail` arm is the rest timer (#4176). The rest timer fires while the user is
// mid-workout, so the tap must land them back on the ACTIVE workout page — not the workouts
// LIST, which forced a redundant "Resume" tap (the reported "extra steps for no benefit").
// `currentPageName` lets the handler skip a redundant push when the workout page is already
// on top (warm resume restored it), which would otherwise stack a duplicate
// WorkoutDetailPage.
export function decideLocalNavigation(extra: LocalNotificationExtra, currentPageName: string | undefined): LocalNavTarget {
  if (extra.page === 'workout-detail') {
    // no workout id to route to (e.g. an older notification scheduled before this fix)
    if (!extra.workoutId) return { target: 'none' };
    // already showing the workout page (warm resume kept it on top) — don't double-push
    if (currentPageName === WORKOUT_DETAIL_PAGE_NAME) return { target: 'none' };
    return { target: 'workout-detail', workoutId: extra.workoutId };
  }
  return decideLogEntryNavigation(extra.page, extra.day, extra.logEntryId);
}

export interface EntrySuppressionKey {
  stringId: string;
  // entry's local date — matches the `day` stored in NotificationEvent.extra and feeds stringToNotificationId, so we can cancel the right pending local notification
  localDay: string;
  // UTC date of the fire time (entry time minus ADVANCE_MINUTES) — matches `now.toISODate()` in tickNotifications, so server suppression lines up with the tick
  utcDay: string;
  // schedule doc _id prefix `${utcWeekday}-${HH:MM}` (e.g. `mon-13:00`). server appends `:${uid}` and does a direct couchGet instead of a by_user view scan.
  scheduleKey: string;
}

// build the identifiers needed to suppress the scheduled reminder for a given log entry:
// stringId + the two date forms (local for client-side id matching, UTC for server-side suppressedDay).
// returns null when the entry type has no reminder config (e.g. DayStart).
export function buildEntrySuppressionKey(entry: Log_Entry, now_local: DateTime): EntrySuppressionKey | null {
  if (!entry.type || !entry.time_utc) return null;

  const config = NOTIFICATION_CONFIGS.find(c => c.logType === entry.type);
  if (!config) return null;

  const entryTime_local = getEntryLocalTimeInDesiredTimeZone(entry, now_local);
  const fireTime_local = entryTime_local.minus({ minutes: ADVANCE_MINUTES });

  const stringId = buildReminderStringId(entry.type, entryTime_local);
  const localDay = entryTime_local.toISODate() ?? '';
  const fireTime_utc = fireTime_local.toUTC();
  const utcDay = fireTime_utc.toISODate() ?? '';
  // schedule doc _id prefix mirrors syncScheduleToServerAsync's utcDay/utcTime construction and server's getMinuteKey
  const utcWeekdayName = WEEKDAY_NAMES[fireTime_utc.weekday - 1];
  const utcTime = `${String(fireTime_utc.hour).padStart(2, '0')}:${String(fireTime_utc.minute).padStart(2, '0')}`;
  const scheduleKey = `${utcWeekdayName}-${utcTime}`;

  if (!localDay || !utcDay) return null;
  return { stringId, localDay, utcDay, scheduleKey };
}

export interface ReminderRestore {
  // the on-device reminder to schedule again — same id as the one the suppress cancelled
  notification: NotificationEvent;
  // the identifiers the server needs to clear today's suppressedDay stamp
  key: EntrySuppressionKey;
}

// decide whether undoing an action on `entry` should bring its reminder back, and if so build
// what to restore. the entry must already carry its restored state. returns null — restore
// nothing — when the entry type has no reminder, the user turned that reminder type off, the
// entry is still Done or Skipped, or the reminder's fire time has already passed: a reminder the
// user has already been shown is never shown again. see design-docs/push-notifications.md,
// "Undoing an action brings the reminder back".
export function decideReminderRestore(
  entry: Log_Entry,
  settings: SettingsService,
  now_local: DateTime,
  getBody?: (contentKey: string) => string,
): ReminderRestore | null {
  const key = buildEntrySuppressionKey(entry, now_local);
  if (!key) return null;
  // the local-schedule rules decide the rest (type enabled, not acted, fire time still ahead),
  // so a restored reminder is exactly the one the next app-open reschedule would build
  const [notification] = buildNotificationsFromLogEntries([entry], settings, now_local, getBody, { excludeActedEntries: true });
  if (!notification) return null;
  return { notification, key };
}

export interface BuildNotificationsOptions {
  // if true, skip entries the user has already completed (Done) or Skipped. Used only for the
  // on-device (local) schedule, so a done/skipped occurrence is not re-added on an app-open
  // reschedule (#4087). The server schedule leaves this false on purpose — see the call site in
  // rescheduleAllAsync for why (recurring weekly doc must survive; today is silenced per-day by
  // the suppressedDay stamp instead).
  excludeActedEntries?: boolean;
}

export function buildNotificationsFromLogEntries(
  entries: Log_Entry[],
  settings: SettingsService,
  now_local: DateTime,
  getBody?: (contentKey: string) => string,
  options?: BuildNotificationsOptions,
): NotificationEvent[] {
  const notifications: NotificationEvent[] = [];
  const excludeActedEntries = options?.excludeActedEntries ?? false;

  for (const entry of entries) {
    if (!entry._id || !entry.type || !entry.time_utc) continue;

    const config = NOTIFICATION_CONFIGS.find(c => c.logType === entry.type);
    if (!config) continue;

    // if the caller asked to exclude acted entries, drop anything the user already completed or
    // skipped. this is the on-device (local) schedule's rule (#4087): the user dealt with this
    // occurrence, so its "time to eat" reminder must not be re-added on the next app-open
    // reschedule. the server schedule does NOT pass this flag — it keeps the entry so its recurring
    // weekly doc survives, and today's fire is silenced per-day by suppressForEntryAsync's
    // suppressedDay stamp instead of by deleting the doc.
    if (excludeActedEntries && (entry.state === LogRecordState.Done || entry.state === LogRecordState.Skipped)) continue;

    // check if this notification type is enabled
    if (!settings[config.settingsKey]) continue;

    const entryTime_local = getEntryLocalTimeInDesiredTimeZone(entry, now_local);
    const fireTime_local = entryTime_local.minus({ minutes: ADVANCE_MINUTES });
    const fireDate = fireTime_local.toJSDate();

    // skip notifications in the past
    if (fireDate.getTime() <= now_local.toMillis()) continue;

    // build deterministic string ID for future server-push dedup
    const dateISO = entryTime_local.toISODate();
    const stringId = buildReminderStringId(entry.type, entryTime_local);
    const numericId = stringToNotificationId(`${stringId}-${dateISO}`);

    const body = getBody ? getBody(config.contentKey) : config.contentKey;

    notifications.push({
      id: numericId,
      title: APP_TITLE,
      body,
      schedule: { at: fireDate },
      extra: { page: config.deepLinkPage, stringId, logEntryId: entry._id, day: dateISO },
    });
  }

  return notifications;
}

// what obtainFcmTokenAsync ended with
export enum FcmTokenOutcome {
  Registered = 'Registered',
  SkippedOffline = 'SkippedOffline',
  Reported = 'Reported',
}

// everything obtainFcmTokenAsync touches, passed in so it can be unit-tested without Capacitor
export interface FcmTokenDeps {
  // asks the native FcmTokenPlugin for the token; rejects when Firebase has none to give
  getToken: () => Promise<string>;
  // sends the token to the server, fire-and-forget
  registerToken: (token: string) => void;
  // the pause between two tries
  wait: (ms: number) => Promise<void>;
  // a fresh check of the connection, not a cached belief; true only when offline
  isOfflineAsync: () => Promise<boolean>;
  // files the Sentry problem
  reportProblem: (context: { error: string; offlineCheckError?: string; msSinceFirstAttempt: number }) => void;
  // the current time in milliseconds, so a report can say how long the tries took
  now: () => number;
}

// on iOS, fetch the FCM token once APNs registration has answered and send it to the server.
// the short retry covers Firebase's internal APNs-to-FCM exchange latency.
export async function obtainFcmTokenAsync(deps: FcmTokenDeps, attempts = 5): Promise<FcmTokenOutcome> {
  const startedAt = deps.now();
  // kept so the report after the last attempt says why the token never came
  let lastError: unknown;
  for (let attempt = 0; attempt < attempts; attempt++) {
    try {
      const token = await deps.getToken();
      deps.registerToken(token);
      return FcmTokenOutcome.Registered;
    } catch (err) {
      lastError = err;
      console.log(`NotificationService: FCM getToken attempt ${attempt + 1} failed:`, err);
      // no pause after the final try: it would only delay the offline check below
      if (attempt < attempts - 1) await deps.wait(1000);
    }
  }
  // while iOS has the app suspended, the 1-second waits can stretch to an hour, so the tries
  // can span a long offline stretch. the report says how long they took.
  const msSinceFirstAttempt = (): number => deps.now() - startedAt;
  // Firebase needs the network to mint a token, so every try fails on a phone that is offline.
  // that is expected and the next app open retries, so it is logged and not reported (#6123).
  // if the check itself fails, report anyway: a broken check must never hide real breakage.
  let isOffline: boolean;
  try {
    isOffline = await deps.isOfflineAsync();
  } catch (checkErr) {
    console.error('NotificationService: FCM token not available after APNs registration, and the offline check failed:', checkErr);
    deps.reportProblem({ error: describeError(lastError), offlineCheckError: describeError(checkErr), msSinceFirstAttempt: msSinceFirstAttempt() });
    return FcmTokenOutcome.Reported;
  }
  if (isOffline) {
    console.log('NotificationService: FCM token not fetched because the device is offline; the next app open retries');
    return FcmTokenOutcome.SkippedOffline;
  }
  // the check runs after the tries, so the phone may have been offline for all of them and only
  // just reconnected (CYBERNETIC-FITNESS-APP-474). if one more try now works, nothing is broken.
  try {
    const token = await deps.getToken();
    console.log('NotificationService: FCM getToken succeeded on the try after the online check');
    deps.registerToken(token);
    return FcmTokenOutcome.Registered;
  } catch (err) {
    lastError = err;
  }
  console.error('NotificationService: FCM token not available after APNs registration');
  deps.reportProblem({ error: describeError(lastError), msSinceFirstAttempt: msSinceFirstAttempt() });
  return FcmTokenOutcome.Reported;
}

// how the wait for Firebase sign-in ended, before a reminder call goes to the server
export enum SignInWait {
  // Firebase settled and somebody is signed in: send the call
  SignedIn = 'SignedIn',
  // Firebase settled and nobody is signed in any more: there is nothing to send for
  SignedOut = 'SignedOut',
  // Firebase did not settle within the wait: send anyway, so a failure is still reported
  NotSettled = 'NotSettled',
}

// everything waitForSignInAsync touches, passed in so it can be unit-tested without Firebase
export interface SignInWaitDeps {
  // resolves once Firebase has settled its sign-in state (AuthService.whenFirebaseAuthSettled)
  settled: Promise<void>;
  // true when the local login was cleared or Firebase has no user (AuthService.isLikelySignOutRace)
  isSignedOut: () => boolean;
  // how long to wait for `settled`
  waitMs: number;
}

// on a warm resume the app signs the user in from the cached login at once, but Firebase's own
// user only appears when its first auth callback fires, seconds later. a reminder call sent in
// that gap has no sign-in token and fails (df-d57fd669a03f, df-c4892fdc42b5), so the reminder calls
// wait here first. no user action waits on those calls, so a long wait costs nothing visible. see
// design-docs/push-notifications.md.
export async function waitForSignInAsync(deps: SignInWaitDeps): Promise<SignInWait> {
  try {
    await timeout(deps.waitMs, deps.settled);
  } catch (err) {
    if ((err as Error)?.name !== 'TimeoutError') throw err;
    return SignInWait.NotSettled;
  }
  return deps.isSignedOut() ? SignInWait.SignedOut : SignInWait.SignedIn;
}

// everything registerPushTokenAsync touches, passed in so it can be unit-tested without Capacitor
export interface PushTokenRegistrationDeps {
  // waits for Firebase sign-in to settle before the call (waitForSignInAsync)
  waitForSignIn: () => Promise<SignInWait>;
  // the remindersRegisterDevice cloud call
  register: () => Promise<unknown>;
  // files the Sentry problem
  reportProblem: (context: { error: string; signInSettled: boolean }) => void;
}

// send the push token to the server. nothing waits on this, so it must never reject: a rejection
// here reached the global unhandled-rejection listener and was filed a second time (#6233).
// a failed registration costs only this app open; the next one registers again.
export async function registerPushTokenAsync(deps: PushTokenRegistrationDeps): Promise<void> {
  let signIn = SignInWait.NotSettled;
  try {
    signIn = await deps.waitForSignIn();
    // if nobody is signed in any more, the sign-out path owns the teardown
    if (signIn === SignInWait.SignedOut) {
      console.log('NotificationService: push token not registered, nobody is signed in');
      return;
    }
    await deps.register();
  } catch (err) {
    // if the cloud-functions layer already filed the one report (a timeout, a lost connection,
    // a 502/503 after its retry), or skipped it because the phone was offline, only log it
    if (wasReportedByCloudFunctions(err)) {
      console.log('NotificationService: push token registration failed:', err);
      return;
    }
    // anything else (another HTTP error, no sign-in token, an unreadable reply) reached here unreported.
    // signInSettled false means Firebase never settled, so the failure echoes that stall
    deps.reportProblem({ error: describeError(err), signInSettled: signIn !== SignInWait.NotSettled });
  }
}
